import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import CustomSelect from "../../Components/common/CustomSelect/CustomSelect";
import CustomTable from "../../Components/common/Table/CustomTable";
import ListPopup from "../../Components/PagesComponents/Dashboard/ListPopup/ListPopup";
import ActionButton from "../../Components/common/ActionButton/ActionButton";
import FileIcon from "../../Components/Icon-SVG/FileIcon";
import DoCdr from "../../Components/common/DoCdr/DoCdr";
import {
  DetailEmpty,
  DetailField,
  DetailGrid,
  DetailItem,
  DetailNote,
  DetailPanel,
  DetailSection,
} from "../../Components/common/DetailPanel/DetailPanel";

import { blockedMailsHeader } from "../../utils/constants/tableHeaders";
import { RequestBlockedEmails, RequestDistinctHosts } from "../../store/actions/mailGatewayActions";

import { checkCdrFromExtension } from "./util";

import "./styles.scss";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

// api_resp is the analysis result of one attachment
const ATTACHMENT_RESULT_TONES = { Clean: "clean", Malicious: "malicious", Suspicious: "suspicious" };

const AttachmentResult = ({ result }) => (
  <div className="verdict-container">
    <p className={`verdict verdict--${ATTACHMENT_RESULT_TONES[result] || "suspicious"}`}>
      {result || "Other"}
    </p>
  </div>
);

const Details = ({ row, history }) => {
  const submissions = row?.original?.files || [];
  const detail = row?.original?.detail || "";

  const seeReportHandler = (groupID) => {
    history.push(`/mailgateway/blocked-emails?data=${groupID}&table=blocked-emails`);
  };

  return (
    <DetailPanel>
      {detail ? <DetailNote tone="danger" title="Why it was blocked">{detail}</DetailNote> : null}
      {submissions.length ? (
        <DetailSection title="Attachments" count={submissions.length}>
          {submissions.map((submission, i) => (
            <DetailItem
              key={i}
              actions={
                <>
                  <ActionButton
                    icon={<FileIcon width={13} height={13} color="currentColor" />}
                    disabled={submission.group_id === "-"}
                    onClick={() => seeReportHandler(submission.group_id)}>
                    See Report
                  </ActionButton>
                  {checkCdrFromExtension(submission?.file_name?.split(".")?.pop()) ? (
                    <DoCdr
                      buttonVariant="action"
                      fileHash={submission?.file_hash}
                      fileId={row?.original?.email_id}
                      marginal={"0em"}
                      severity={submission.api_resp === "Clean" ? "not suspicious" : "malicious"}
                      fileExt={submission?.file_name?.split(".")?.pop()}
                    />
                  ) : null}
                </>
              }>
              <DetailGrid columns={3}>
                <DetailField label="File name">{submission.file_name}</DetailField>
                <DetailField label="SHA256" mono>
                  {submission?.file_hash}
                </DetailField>
                <DetailField label="Analysis result">
                  <AttachmentResult result={submission.api_resp} />
                </DetailField>
              </DetailGrid>
            </DetailItem>
          ))}
        </DetailSection>
      ) : null}
      {!detail && !submissions.length ? (
        <DetailEmpty>No further detail was recorded for this email.</DetailEmpty>
      ) : null}
    </DetailPanel>
  );
};

const BlockedEmails = () => {
  const [selectedHost, setSelectedHost] = useState(null);
  const [listPopupActive, setListPopupActive] = useState(false);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState(null);

  const dispatch = useDispatch();

  const distinctHosts = useSelector((state) => state.mailGateway.distinctHosts);
  const distinctHostsLoading = useSelector((state) => state.mailGateway.distinctHostsLoading);
  const blockedEmails = useSelector((state) => state.mailGateway.blockedEmails);
  const blockedEmailsLoading = useSelector((state) => state.mailGateway.blockedEmailsLoading);

  let query = useQuery();

  useEffect(() => {
    if (query.get("data") !== null) {
      setListPopupActive(true);
    } else {
      setListPopupActive(false);
    }
  }, [query]);

  useEffect(() => {
    dispatch(RequestDistinctHosts());
  }, [dispatch]);

  useEffect(() => {
    if (selectedHost?.id && selectedTimeFilter) {
      dispatch(RequestBlockedEmails({ host: selectedHost.id, timeFilter: selectedTimeFilter }));
    }
  }, [dispatch, selectedHost, selectedTimeFilter]);

  return (
    <div className="blocked-emails">
      <div className="blocked-emails__header">
        <h1>Blocked Emails</h1>
        <p>Below list is displaying blocked emails of selected host:</p>
        <div className="blocked-emails__header__select">
          <p>Select host: </p>
          <CustomSelect
            id="mgwBlockedEmails"
            data={distinctHosts.length ? distinctHosts : []}
            select={selectedHost}
            setSelected={setSelectedHost}
            width={220}
            disabled={distinctHosts.length === 0}
            placeholder={distinctHostsLoading ? "Fetching data..." : "No data."}
          />
        </div>
      </div>
      <div className="blocked-emails__table border">
        <CustomTable
          leftAction={true}
          renderRowSubComponent={Details}
          exportFile={true}
          dataTable={blockedEmails}
          loading={blockedEmailsLoading}
          tableName="blockedEmails"
          column={blockedMailsHeader}
          paginationButtons={true}
          className=""
          customPageSize={10}
          minHeight="585px"
          minBottom="5px"
          defaultPosition="relative"
          selectedHost={selectedHost?.id}
          filterByTime={true}
          selectedTimeFilter={selectedTimeFilter}
          setSelectedTimeFilter={setSelectedTimeFilter}
          defaultFilterIndex={2}
        />
      </div>
      <ListPopup
        onClickOk={() => setListPopupActive(false)}
        show={listPopupActive}
        groupID={query.get("data")}
        tableType={query.get("table")}
        tableType2="blocked-emails"
      />
    </div>
  );
};

export default BlockedEmails;
